/**
 * Sesión, empresas y rol del usuario.
 *
 * El token de Firebase Auth trae dos custom claims: `empresas`, la lista de
 * códigos a los que la cuenta pertenece, y `roles`, qué rol tiene en cada una.
 * Ninguno de los dos vive en un documento que el usuario pueda leer o alterar
 * (§17 del requerimiento). Acá solo se leen para decidir qué mostrar; quien lo
 * hace cumplir de verdad son las reglas de seguridad de Firestore.
 *
 * **El rol es por empresa, no de la persona.** La misma cuenta puede ser
 * administradora en su propio negocio y de solo lectura en el de un cliente al
 * que le da soporte. Un rol global volvería a mezclar las dos cosas.
 *
 * **Cuál empresa se está mirando no se guarda aquí**: sale de la dirección del
 * navegador. Si esta sesión recordara una «empresa activa», sería una segunda
 * fuente capaz de discrepar con lo que muestra la pantalla. Ver `ruta.ts`.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import type { Rol, Usuario } from '@guerrera/core';
import { auth, db, rechazoDeSesion, renovarSesion } from './firebase';
import { mensajeDeFallo } from './errores';
import { useEmpresaDeRuta } from './ruta';

/** Lo que el propio usuario puede editar de su perfil. El rol NO está aquí:
 *  vive en el token y lo pone un administrador con el script de roles. */
export type PerfilEditable = Pick<Usuario, 'nombre' | 'telefono' | 'foto_url'>;

interface Sesion {
  cargando: boolean;
  usuario: User | null;
  /** Las empresas a las que pertenece la cuenta. */
  empresas: string[];
  /** La que se está mirando, tomada de la dirección. */
  empresa: string | null;
  /** `true` si la dirección apunta a una empresa a la que no pertenece. */
  empresaAjena: boolean;
  /** `true` si está mirando una empresa ajena como superadmin. */
  deVisita: boolean;
  /** El rol en la empresa que se está mirando. */
  rol: Rol | null;
  /** El rol en cada empresa a la que pertenece. */
  roles: Record<string, Rol>;
  /** El dueño de la plataforma: ve todas las empresas y da de alta clientes. */
  superadmin: boolean;
  perfil: Partial<Usuario> | null;
  entrar: (correo: string, clave: string) => Promise<void>;
  salir: () => Promise<void>;
  guardarPerfil: (cambios: Partial<PerfilEditable>) => Promise<void>;
}

const Ctx = createContext<Sesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [cargando, setCargando] = useState(true);
  const [usuario, setUsuario] = useState<User | null>(null);
  const [empresas, setEmpresas] = useState<string[]>([]);
  const [roles, setRoles] = useState<Record<string, Rol>>({});
  const [superadmin, setSuperadmin] = useState(false);
  const [perfil, setPerfil] = useState<Partial<Usuario> | null>(null);
  const deRuta = useEmpresaDeRuta();

  useEffect(() => {
    return onAuthStateChanged(auth(), async (u) => {
      if (u) {
        // `true` fuerza refrescar el token: si acaban de asignarle el rol,
        // lo toma sin tener que cerrar y abrir sesión. Sin señal no se puede
        // refrescar: se sigue con el token guardado, y no con la pantalla
        // en «Cargando…» para siempre.
        //
        // El usuario se publica DESPUÉS de tener el token vigente: todo lo que
        // escucha Firestore arranca con `usuario`, y una escucha que arranca
        // con el token vencido la rechaza el servidor y no se reintenta.
        const token = await u.getIdTokenResult(true).catch(() => u.getIdTokenResult()).catch(() => null);
        setUsuario(u);
        if (!token) { setEmpresas([]); setRoles({}); setSuperadmin(false); setCargando(false); return; }
        const lista = token.claims.empresas;
        const mapa = token.claims.roles;
        setEmpresas(Array.isArray(lista) ? (lista as string[]) : []);
        setRoles(
          mapa && typeof mapa === 'object' ? (mapa as Record<string, Rol>) : {},
        );
        setSuperadmin(token.claims.superadmin === true);
      } else {
        setUsuario(null);
        setEmpresas([]);
        setRoles({});
        setSuperadmin(false);
      }
      setCargando(false);
    });
  }, []);

  // El perfil se escucha en vivo: si cambia la foto, la barra lateral se
  // entera sin recargar.
  useEffect(() => {
    if (!usuario) { setPerfil(null); return; }
    let cancelar: (() => void) | null = null;
    let vivo = true;
    // Si el servidor la rechaza por un token viejo, se renueva y se vuelve a
    // escuchar una vez más; solo entonces se da por perdida.
    const escuchar = async (intentos: number): Promise<void> => {
      const fs = await import('firebase/firestore');
      const base = await db();
      if (!vivo) return;
      cancelar = fs.onSnapshot(
        fs.doc(base, 'usuarios', usuario.uid),
        (d) => setPerfil(d.exists() ? (d.data() as Usuario) : {}),
        (e) => {
          if (vivo && intentos < 2 && rechazoDeSesion(e)) {
            void renovarSesion().then(() => {
              if (vivo) window.setTimeout(() => { if (vivo) void escuchar(intentos + 1).catch(() => setPerfil({})); }, 800);
            });
          } else {
            // Lo que ya se mostraba se queda: perder la foto y el nombre porque
            // una lectura falló es peor que mostrarlos un poco viejos.
            setPerfil((p) => p ?? {});
          }
        },
      );
    };
    escuchar(0).catch(() => setPerfil((p) => p ?? {}));
    return () => { vivo = false; cancelar?.(); };
  }, [usuario]);

  // El superadmin puede pararse en cualquier empresa. No hereda rol: entra sin
  // ninguno, así que las pantallas le dejan mirar y no tocar, que es
  // exactamente lo que dicen las reglas de Firestore. Que la interfaz y la base
  // de datos digan lo mismo evita botones que prometen algo que el servidor
  // después rechaza.
  const propia = Boolean(deRuta && empresas.includes(deRuta));
  const empresa = deRuta && (propia || superadmin) ? deRuta : null;
  const rol = empresa && propia ? (roles[empresa] ?? null) : null;

  const valor = useMemo<Sesion>(
    () => ({
      cargando,
      usuario,
      empresas,
      empresa,
      empresaAjena: Boolean(deRuta) && !empresa,
      deVisita: Boolean(empresa) && !propia,
      rol,
      roles,
      superadmin,
      perfil,
      entrar: async (correo, clave) => {
        await signInWithEmailAndPassword(auth(), correo.trim(), clave);
      },
      salir: async () => {
        await signOut(auth());
      },
      guardarPerfil: async (cambios) => {
        if (!usuario) throw new Error('No hay sesión abierta.');
        const fs = await import('firebase/firestore');
        const base = await db();
        // merge y no set: el documento puede no existir todavía, y si existe
        // no queremos borrar el rol ni nada que haya puesto un administrador.
        await fs.setDoc(fs.doc(base, 'usuarios', usuario.uid), cambios, { merge: true });
      },
    }),
    [cargando, usuario, empresas, empresa, propia, deRuta, rol, roles, superadmin, perfil],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion(): Sesion {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSesion debe usarse dentro de ProveedorSesion');
  return s;
}

/**
 * Mensajes de error de Firebase Auth, en palabras de gente.
 *
 * La tabla vive en `errores.ts`, junto a la de Firestore, y no aquí: un error
 * de sesión no solo aparece entrando. El token se vence a media tarde y la
 * falla sale guardando un pedido, así que el traductor tiene que ser uno solo.
 */
export function mensajeDeError(e: unknown): string {
  return mensajeDeFallo(e, 'No se pudo entrar. Intenta de nuevo.');
}
